import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Layout from "@/components/layout/Layout";
import { JOURNAL_CATEGORIES, JournalPost, SITE_URL, formatDate, listPublishedPosts } from "@/lib/journalApi";

const JournalIndex = () => {
  const [posts, setPosts] = useState<JournalPost[] | null>(null);
  const [cat, setCat] = useState<string | null>(null);

  useEffect(() => {
    listPublishedPosts().then(setPosts).catch(() => setPosts([]));
  }, []);

  const featured = useMemo(() => posts?.find((p) => p.featured) ?? posts?.[0], [posts]);
  const usedCats = JOURNAL_CATEGORIES.filter((c) => posts?.some((p) => p.category === c));
  const rest = (posts ?? []).filter((p) => p.id !== featured?.id && (!cat || p.category === cat));

  return (
    <Layout>
      <Helmet>
        <title>Journal | Olivia Flemming, Birmingham Singer</title>
        <meta name="description" content="The Journal from Olivia Flemming: guides to live music for weddings, hotels, care homes and funerals in Birmingham and the West Midlands, plus stories behind her music." />
        <meta property="og:title" content="Journal | Olivia Flemming" />
        <meta property="og:description" content="Guides to live music in Birmingham and the West Midlands, and stories behind Olivia Flemming's music." />
        <meta property="og:url" content={`${SITE_URL}/journal`} />
      </Helmet>

      <section className="bg-background pt-32 md:pt-40 pb-12 md:pb-16 border-b border-gold/15">
        <div className="max-w-5xl mx-auto px-5 text-center">
          <p className="text-gold text-xs tracking-[0.35em] uppercase font-playfair mb-4">Olivia Flemming</p>
          <h1 className="font-playfair text-4xl md:text-6xl text-foreground">The Journal</h1>
          <div className="w-12 h-px bg-gold/70 mx-auto my-6" />
          <p className="font-playfair italic text-foreground/70 text-lg max-w-2xl mx-auto">
            Notes on live music, occasions and the stories behind the songs.
          </p>
        </div>
      </section>

      <section className="bg-background py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-5">
          {posts === null && <p className="text-center text-foreground/50">Loading…</p>}

          {featured && !cat && (
            <Link to={`/journal/${featured.slug}`} className="group grid md:grid-cols-2 gap-6 md:gap-12 items-center mb-16 md:mb-24">
              {featured.cover_image_url && (
                <div className="aspect-[4/3] overflow-hidden border border-gold/25">
                  <img src={featured.cover_image_url} alt={featured.cover_alt ?? featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
              )}
              <div>
                <p className="text-gold text-[11px] tracking-[0.3em] uppercase mb-3">Featured · {featured.category}</p>
                <h2 className="font-playfair text-3xl md:text-4xl text-foreground leading-tight group-hover:text-gold transition-colors">{featured.title}</h2>
                <p className="text-foreground/70 mt-4 leading-relaxed">{featured.excerpt}</p>
                <p className="text-foreground/40 text-sm mt-4">{formatDate(featured.published_at)}</p>
              </div>
            </Link>
          )}

          {usedCats.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {[null, ...usedCats].map((c) => (
                <button
                  key={c ?? "all"}
                  onClick={() => setCat(c)}
                  className={`rounded-full px-4 py-1.5 text-xs tracking-[0.15em] uppercase border transition-colors ${cat === c ? "border-gold text-gold" : "border-foreground/15 text-foreground/55 hover:text-foreground"}`}
                >
                  {c ?? "All"}
                </button>
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {(cat ? (posts ?? []).filter((p) => p.category === cat) : rest).map((p) => (
              <Link key={p.id} to={`/journal/${p.slug}`} className="group block">
                {p.cover_image_url && (
                  <div className="aspect-[4/3] overflow-hidden border border-gold/20 mb-5">
                    <img src={p.cover_image_url} alt={p.cover_alt ?? p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <p className="text-gold text-[11px] tracking-[0.3em] uppercase mb-2">{p.category}</p>
                <h3 className="font-playfair text-xl md:text-2xl text-foreground leading-snug group-hover:text-gold transition-colors">{p.title}</h3>
                <p className="text-foreground/60 text-sm mt-3 leading-relaxed line-clamp-3">{p.excerpt}</p>
                <p className="text-foreground/40 text-xs mt-3">{formatDate(p.published_at)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default JournalIndex;
